// ============================================================
// Kickoff Decks
//
// Build a kickoff presentation for a client who is already in the app, run it
// full-screen in the meeting, and capture goals, timeline dates and the admin
// checklist as you go.
//
// Goals and timeline dates are written into client_goals and
// client_commitments — the same records the 30/60/90 touchpoints read — so the
// dates committed to in the kickoff are the ones tracked later.
// ============================================================
import { useState, useEffect, useMemo, useCallback } from 'react'
import {
  Plus, X, Check, ChevronLeft, ChevronRight, Play, FileText, Target,
  CalendarDays, ClipboardList, Trash2, Presentation,
} from 'lucide-react'
import { supabase } from './supabase'
import { useToast } from './ToastContext'
import {
  SLIDE_TEMPLATES, getSlide, CHECKLIST, CHECKLIST_STATES, checklistStats,
  defaultSlidesFor, DECK_STATUS, BRAND_FIELDS,
} from './kickoffDeckConfig'

const fmt = d => d ? new Date(String(d).slice(0, 10) + 'T12:00:00')
  .toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

const linesOf = v => String(v || '').split('\n').map(x => x.trim()).filter(Boolean)

// ── One slide, rendered for the meeting ─────────────────────────────────────
function SlideView({ slideKey, data, client, goals, commitments, big, editing, onSaveClient, onSaveSlide }) {
  const def = getSlide(slideKey)
  if (!def) return null
  const S = big ? 1 : 0.62   // scale for the small preview

  const Title = ({ children }) => (
    <div style={{ fontFamily: 'var(--font-display)', fontSize: 34 * S, color: '#fff',
      lineHeight: 1.15 }}>{children}</div>
  )
  // An editable line that sits in the slide without looking like a form until
  // it has focus — so the client sees the deck, not an admin screen.
  const Inline = ({ value, placeholder, onSave, size = 14, rows = 2 }) => (
    <textarea
      defaultValue={value || ''}
      placeholder={placeholder}
      rows={rows}
      onBlur={e => { if (e.target.value !== (value || '')) onSave(e.target.value) }}
      style={{
        width: '100%', border: '1px solid transparent', borderRadius: 6,
        padding: `${4 * S}px ${6 * S}px`, marginLeft: -6 * S,
        fontFamily: 'var(--font-body)', fontSize: size * S, lineHeight: 1.5,
        color: value ? 'var(--text-primary)' : 'var(--text-muted)',
        fontStyle: value ? 'normal' : 'italic',
        background: 'transparent', resize: 'vertical', outline: 'none',
      }}
      onFocus={e => { e.target.style.background = '#fffdf7'; e.target.style.borderColor = 'var(--gold)' }}
      onBlurCapture={e => { e.target.style.background = 'transparent'; e.target.style.borderColor = 'transparent' }}
    />
  )

  const Bullets = ({ items, size = 17 }) => (
    <ul style={{ margin: 0, paddingLeft: 22 * S, lineHeight: 1.65 }}>
      {items.map((t, i) => <li key={i} style={{ fontSize: size * S, marginBottom: 5 * S }}>{t}</li>)}
    </ul>
  )

  const body = () => {
    switch (slideKey) {
      case 'dashboard':
        return (
          <>
            {data.brand_note && (
              <div style={{ fontSize: 15 * S, lineHeight: 1.6, marginBottom: 12 * S }}>
                {data.brand_note}
              </div>
            )}
            {BRAND_FIELDS.map(f => {
              const v = client?.[f.key]
              return (
                <div key={f.key} style={{ marginBottom: 8 * S }}>
                  <div style={{ fontSize: 11 * S, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.4, color: 'var(--gold)' }}>{f.label}</div>
                  {editing && onSaveClient ? (
                    <Inline value={v} placeholder={f.hint}
                      onSave={val => onSaveClient({ [f.key]: val || null })} />
                  ) : (
                    <div style={{ fontSize: 14 * S, lineHeight: 1.5,
                      color: v ? 'var(--text-primary)' : 'var(--text-muted)',
                      fontStyle: v ? 'normal' : 'italic' }}>
                      {v || 'Not yet captured'}
                    </div>
                  )}
                </div>
              )
            })}
          </>
        )
      case 'goals':
        return (
          <>
            {data.intro && <div style={{ fontSize: 17 * S, marginBottom: 14 * S }}>{data.intro}</div>}
            {goals.length === 0 ? (
              <div style={{ fontSize: 15 * S, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No goals set yet — add them in the Goals tab and they appear here.
              </div>
            ) : goals.map(g => (
              <div key={g.id} style={{ marginBottom: 12 * S, paddingBottom: 10 * S,
                borderBottom: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: 19 * S, fontWeight: 600, color: 'var(--navy)' }}>{g.title}</div>
                {g.current_state && (
                  <div style={{ fontSize: 13.5 * S, color: 'var(--text-muted)', marginTop: 3 * S }}>
                    <strong>Today:</strong> {g.current_state}
                  </div>
                )}
                {g.target_text && (
                  <div style={{ fontSize: 14.5 * S, color: 'var(--text-secondary)', marginTop: 2 * S }}>
                    <strong>Aiming for:</strong> {g.target_text}
                  </div>
                )}
                <div style={{ fontSize: 13 * S, color: 'var(--text-muted)', marginTop: 2 * S }}>
                  {g.target != null && `Target ${g.target}`}
                  {g.target != null && g.metric ? ` ${g.metric}` : ''}
                  {g.target_date ? `${g.target != null ? ' · ' : ''}by ${fmt(g.target_date)}` : ''}
                </div>
              </div>
            ))}
          </>
        )

      case 'timeline':
        return (
          <>
            {data.intro && <div style={{ fontSize: 17 * S, marginBottom: 14 * S }}>{data.intro}</div>}
            {commitments.length === 0 ? (
              <div style={{ fontSize: 15 * S, color: 'var(--text-muted)', fontStyle: 'italic' }}>
                No dates yet — add them in the Timeline tab.
              </div>
            ) : commitments.map(c => (
              <div key={c.id} style={{ display: 'flex', gap: 14 * S, alignItems: 'baseline',
                marginBottom: 9 * S }}>
                <div style={{ width: 128 * S, flexShrink: 0, fontSize: 15 * S, fontWeight: 600,
                  color: 'var(--gold)' }}>{fmt(c.due_date)}</div>
                <div style={{ fontSize: 16 * S }}>{c.what}</div>
              </div>
            ))}
          </>
        )

      case 'expectations':
        return (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 * S }}>
            {[['Month 1', 'month1'], ['Months 2-3', 'month2_3'], ['Months 4-6', 'month4_6']].map(([lbl, k]) => (
              linesOf(data[k]).length > 0 && (
                <div key={k}>
                  <div style={{ fontSize: 12 * S, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.5, color: 'var(--gold)', marginBottom: 5 * S }}>{lbl}</div>
                  <Bullets items={linesOf(data[k])} size={15} />
                </div>
              )
            ))}
            {linesOf(data.not_yet).length > 0 && (
              <div style={{ gridColumn: '1 / -1', marginTop: 6 * S, padding: `${11 * S}px ${13 * S}px`,
                background: '#fff8e1', borderRadius: 8, border: '1px solid #f0d98a' }}>
                <div style={{ fontSize: 12 * S, fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5, color: '#8a6000', marginBottom: 5 * S }}>
                  What you will NOT see yet
                </div>
                <Bullets items={linesOf(data.not_yet)} size={15} />
              </div>
            )}
          </div>
        )

      default:
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 13 * S }}>
            {def.fields.map(f => {
              const v = data[f.key]
              if (!v && !editing) return null
              return (
                <div key={f.key}>
                  <div style={{ fontSize: 12 * S, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.5, color: 'var(--gold)', marginBottom: 4 * S }}>{f.label}</div>
                  {editing && onSaveSlide ? (
                    <Inline value={v} size={15} rows={f.type === 'lines' ? 4 : 2}
                      placeholder={f.type === 'lines' ? 'One per line' : f.label}
                      onSave={val => onSaveSlide(f.key, val)} />
                  ) : f.type === 'lines'
                    ? <Bullets items={linesOf(v)} />
                    : <div style={{ fontSize: 16 * S, lineHeight: 1.6 }}>{v}</div>}
                </div>
              )
            })}
          </div>
        )
    }
  }

  return (
    <div style={{ display: 'flex', aspectRatio: '16 / 9', background: '#fff',
      borderRadius: big ? 0 : 10, overflow: 'hidden', border: big ? 'none' : '1px solid var(--border)' }}>
      <div style={{ width: '34%', background: 'var(--navy)', padding: `${34 * S}px ${26 * S}px`,
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
        <Title>{def.name}</Title>
        <div style={{ fontSize: 12 * S, color: 'rgba(255,255,255,0.55)' }}>
          {client?.name}{data._meeting_date ? ` · ${fmt(data._meeting_date)}` : ''}
        </div>
      </div>
      <div style={{ flex: 1, padding: `${34 * S}px ${30 * S}px`, overflow: 'auto' }}>
        {body()}
      </div>
    </div>
  )
}

// ── Meeting mode ────────────────────────────────────────────────────────────
// Full screen, and deliberately not a slideshow. Reads like a page: big
// editorial type, sections down a rail, and talking points that light up as
// you speak to them. A side panel holds notes and brand capture and stays put
// no matter which section you're on.

// Click-to-edit region that reads as content until you touch it.
function Editable({ value, onSave, placeholder, size = 15, weight, lines, color, dark }) {
  const [editing, setEditing] = useState(false)
  const [hover, setHover] = useState(false)
  const [v, setV] = useState(value || '')
  const [saved, setSaved] = useState(false)
  useEffect(() => { setV(value || '') }, [value])

  const commit = async () => {
    setEditing(false)
    if (v === (value || '')) return
    const ok = await onSave(v)
    if (ok !== false) { setSaved(true); setTimeout(() => setSaved(false), 1800) }
  }

  if (editing) {
    return (
      <textarea autoFocus value={v} rows={lines ? 6 : 2}
        onChange={e => setV(e.target.value)} onBlur={commit}
        onKeyDown={e => { if (e.key === 'Escape') { setV(value || ''); setEditing(false) } }}
        style={{ width: '100%', fontFamily: 'var(--font-body)', fontSize: size, lineHeight: 1.6,
          fontWeight: weight, color: 'var(--text-primary)', padding: '7px 10px', margin: '-7px -10px',
          borderRadius: 8, resize: 'vertical', border: '1.5px solid var(--gold)',
          background: '#fffdf7', outline: 'none' }} />
    )
  }

  const empty = !value || !String(value).trim()
  return (
    <div onClick={() => setEditing(true)}
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      title="Click to edit"
      style={{ position: 'relative', cursor: 'text', borderRadius: 8, padding: '7px 10px',
        margin: '-7px -10px',
        background: hover ? (dark ? 'rgba(255,255,255,0.10)' : 'rgba(196,150,42,0.10)') : 'transparent',
        boxShadow: hover ? `inset 0 0 0 1px ${dark ? 'rgba(255,255,255,0.25)' : 'rgba(196,150,42,0.35)'}` : 'none',
        transition: 'background .12s, box-shadow .12s' }}>
      {empty ? (
        <span style={{ fontSize: size, fontStyle: 'italic',
          color: dark ? 'rgba(255,255,255,0.45)' : 'var(--text-muted)' }}>
          {placeholder || 'Click to add'}
        </span>
      ) : (
        <div style={{ fontSize: size, fontWeight: weight, lineHeight: 1.6, whiteSpace: 'pre-wrap',
          color: color || (dark ? '#fff' : 'var(--text-primary)') }}>{value}</div>
      )}
      {saved && <span style={{ position: 'absolute', top: 2, right: 4, fontSize: 11,
        fontWeight: 700, color: 'var(--green)' }}>✓</span>}
    </div>
  )
}

// The hook: each point is a row that lifts and colors as you hover it, while
// the others recede. Click one to mark it covered so you can see where you are.
function TalkingPoints({ value, onSave, covered, onToggleCovered, idPrefix }) {
  const [editing, setEditing] = useState(false)
  const [v, setV] = useState(value || '')
  const [hoverIdx, setHoverIdx] = useState(null)
  useEffect(() => { setV(value || '') }, [value])

  const items = linesOf(value)

  if (editing) {
    return (
      <textarea autoFocus value={v} rows={Math.max(5, items.length + 2)}
        onChange={e => setV(e.target.value)}
        onBlur={async () => { setEditing(false); if (v !== (value || '')) await onSave(v) }}
        style={{ width: '100%', fontFamily: 'var(--font-body)', fontSize: 17, lineHeight: 1.7,
          padding: 14, borderRadius: 10, border: '1.5px solid var(--gold)', background: '#fffdf7',
          resize: 'vertical', outline: 'none' }} />
    )
  }

  if (!items.length) {
    return (
      <div onClick={() => setEditing(true)}
        style={{ fontSize: 16, fontStyle: 'italic', color: 'var(--text-muted)', cursor: 'text',
          padding: '10px 12px', border: '1px dashed var(--border)', borderRadius: 10 }}>
        Click to add talking points — one per line
      </div>
    )
  }

  return (
    <div>
      {items.map((t, idx) => {
        const id = `${idPrefix}-${idx}`
        const done = covered.has(id)
        const active = hoverIdx === idx
        const dim = hoverIdx !== null && !active
        return (
          <div key={id}
            onMouseEnter={() => setHoverIdx(idx)}
            onMouseLeave={() => setHoverIdx(null)}
            onClick={() => onToggleCovered(id)}
            style={{ display: 'flex', alignItems: 'flex-start', gap: 14, cursor: 'pointer',
              padding: '13px 16px', marginBottom: 7, borderRadius: 12,
              background: active ? 'var(--navy)' : done ? 'var(--lightgreen-bg)' : 'var(--cream)',
              color: active ? '#fff' : 'var(--text-primary)',
              opacity: dim ? 0.42 : 1,
              transform: active ? 'translateX(5px)' : 'none',
              boxShadow: active ? '0 6px 20px rgba(22,36,61,0.28)' : 'none',
              transition: 'all .16s ease' }}>
            <span style={{ width: 21, height: 21, borderRadius: 50, flexShrink: 0, marginTop: 2,
              display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11.5,
              fontWeight: 700,
              background: done ? 'var(--green)' : active ? 'var(--gold)' : 'rgba(0,0,0,0.07)',
              color: done || active ? '#fff' : 'var(--text-muted)' }}>
              {done ? '✓' : idx + 1}
            </span>
            <span style={{ fontSize: active ? 20 : 18, lineHeight: 1.45, fontWeight: active ? 500 : 400,
              transition: 'font-size .16s' }}>{t}</span>
          </div>
        )
      })}
      <button className="btn btn-ghost btn-sm" style={{ marginTop: 4 }}
        onClick={() => setEditing(true)}>Edit these points</button>
    </div>
  )
}

function MeetingMode({
  deck, client, goals, commitments,
  onClose, onSaveClient, onSaveSlide, onSaveDeck, onAddGoal, onAddCommitment,
}) {
  const order = deck.slide_order || []
  const [i, setI] = useState(0)
  const [covered, setCovered] = useState(new Set())
  const [panel, setPanel] = useState('notes')
  const [adding, setAdding] = useState(false)
  const [goalDraft, setGoalDraft] = useState({ title: '', current_state: '', target_text: '', target_date: '' })
  const [tlDraft, setTlDraft] = useState({ what: '', due_date: '' })

  const key = order[i]
  const def = getSlide(key)
  const data = deck.slides?.[key] || {}

  const toggleCovered = (id) => setCovered(c => {
    const n = new Set(c); n.has(id) ? n.delete(id) : n.add(id); return n
  })

  useEffect(() => {
    const onKey = e => {
      const t = e.target.tagName
      if (t === 'TEXTAREA' || t === 'INPUT') return
      if (e.key === 'ArrowRight') setI(x => Math.min(order.length - 1, x + 1))
      if (e.key === 'ArrowLeft') setI(x => Math.max(0, x - 1))
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [order.length, onClose])

  const fillOf = (k) => {
    const d = getSlide(k)
    if (!d) return 0
    if (d.isBrand) return BRAND_FIELDS.filter(f => client?.[f.key]).length / BRAND_FIELDS.length
    if (d.isGoals) return goals.length ? 1 : 0
    if (d.isTimeline) return commitments.length ? 1 : 0
    const fs = d.fields || []
    return fs.length ? fs.filter(f => (deck.slides?.[k] || {})[f.key]).length / fs.length : 1
  }

  const Eyebrow = ({ children }) => (
    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8,
      color: 'var(--gold)', marginBottom: 8 }}>{children}</div>
  )

  const main = () => {
    if (def?.isBrand) {
      return (
        <>
        {data.brand_note && (
          <div style={{ fontSize: 15.5, lineHeight: 1.6, marginBottom: 22, maxWidth: 640 }}>
            {data.brand_note}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '18px 30px' }}>
          {BRAND_FIELDS.map(f => (
            <div key={f.key} style={{ padding: '15px 17px', background: 'var(--cream)',
              borderRadius: 12 }}>
              <Eyebrow>{f.label}</Eyebrow>
              <Editable value={client?.[f.key]} size={15.5} placeholder={f.hint}
                onSave={val => onSaveClient({ [f.key]: val || null })} />
            </div>
          ))}
        </div>
        </>
      )
    }

    if (def?.isGoals) {
      return (
        <>
          {goals.map(g => (
            <div key={g.id} style={{ padding: '18px 22px', background: 'var(--cream)',
              borderRadius: 12, marginBottom: 12 }}>
              <div style={{ fontFamily: 'var(--font-display)', fontSize: 24, color: 'var(--navy)' }}>
                {g.title}
              </div>
              <div style={{ display: 'flex', gap: 34, marginTop: 11, flexWrap: 'wrap' }}>
                {g.current_state && (
                  <div><Eyebrow>Today</Eyebrow>
                    <div style={{ fontSize: 16 }}>{g.current_state}</div></div>
                )}
                {g.target_text && (
                  <div><Eyebrow>Aiming for</Eyebrow>
                    <div style={{ fontSize: 16 }}>{g.target_text}</div></div>
                )}
                {g.target_date && (
                  <div><Eyebrow>By</Eyebrow>
                    <div style={{ fontSize: 16, color: 'var(--gold)', fontWeight: 600 }}>
                      {fmt(g.target_date)}</div></div>
                )}
              </div>
            </div>
          ))}
          {!adding ? (
            <button className="btn btn-ghost btn-sm" onClick={() => setAdding(true)}>
              <Plus size={12} /> Add a goal
            </button>
          ) : (
            <div style={{ border: '1.5px solid var(--gold)', borderRadius: 12, padding: 16,
              background: '#fffdf7' }}>
              {[['title', 'What we are aiming for'], ['current_state', 'Where you are today'],
                ['target_text', 'What success looks like']].map(([k2, label]) => (
                <div key={k2} style={{ marginBottom: 9 }}>
                  <label className="form-label">{label}</label>
                  <input className="form-control" value={goalDraft[k2]}
                    onChange={e => setGoalDraft({ ...goalDraft, [k2]: e.target.value })} />
                </div>
              ))}
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                <div style={{ width: 165 }}>
                  <label className="form-label">By when</label>
                  <input className="form-control" type="date" value={goalDraft.target_date}
                    onChange={e => setGoalDraft({ ...goalDraft, target_date: e.target.value })} />
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
                <button className="btn btn-gold btn-sm" disabled={!goalDraft.title.trim()}
                  onClick={async () => {
                    const ok = await onAddGoal(goalDraft)
                    if (ok !== false) {
                      setGoalDraft({ title: '', current_state: '', target_text: '', target_date: '' })
                      setAdding(false)
                    }
                  }}>Add</button>
              </div>
            </div>
          )}
        </>
      )
    }

    if (def?.isTimeline) {
      return (
        <>
          {commitments.map((c, idx) => (
            <div key={c.id} style={{ display: 'flex', gap: 22, alignItems: 'center',
              padding: '15px 20px', background: idx % 2 ? 'transparent' : 'var(--cream)',
              borderRadius: 12, marginBottom: 4 }}>
              <div style={{ width: 150, flexShrink: 0, fontFamily: 'var(--font-display)',
                fontSize: 19, color: 'var(--gold)' }}>{fmt(c.due_date)}</div>
              <div style={{ fontSize: 18 }}>{c.what}</div>
            </div>
          ))}
          {!adding ? (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }}
              onClick={() => setAdding(true)}><Plus size={12} /> Add a date</button>
          ) : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 14 }}>
              <div style={{ flex: 1 }}>
                <label className="form-label">What are we delivering?</label>
                <input className="form-control" value={tlDraft.what}
                  onChange={e => setTlDraft({ ...tlDraft, what: e.target.value })} />
              </div>
              <div style={{ width: 155 }}>
                <label className="form-label">By when</label>
                <input className="form-control" type="date" value={tlDraft.due_date}
                  onChange={e => setTlDraft({ ...tlDraft, due_date: e.target.value })} />
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => setAdding(false)}>Cancel</button>
              <button className="btn btn-gold btn-sm"
                disabled={!tlDraft.what.trim() || !tlDraft.due_date}
                onClick={async () => {
                  const ok = await onAddCommitment(tlDraft)
                  if (ok !== false) { setTlDraft({ what: '', due_date: '' }); setAdding(false) }
                }}>Add</button>
            </div>
          )}
        </>
      )
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
        {(def?.fields || []).map(f => (
          <div key={f.key}>
            <Eyebrow>{f.label}</Eyebrow>
            {f.type === 'lines' ? (
              <TalkingPoints value={data[f.key]} idPrefix={`${key}-${f.key}`}
                covered={covered} onToggleCovered={toggleCovered}
                onSave={val => onSaveSlide(key, f.key, val)} />
            ) : (
              <Editable value={data[f.key]} size={17} placeholder={f.label}
                onSave={val => onSaveSlide(key, f.key, val)} />
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 200, background: '#fff',
      display: 'flex', flexDirection: 'column' }}>

      {/* Slim bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 20px',
        height: 54, borderBottom: '1px solid var(--border-light)', flexShrink: 0 }}>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 18, color: 'var(--navy)' }}>
          {client?.name}
        </span>
        <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
          Kick Off{deck.meeting_date ? ` · ${fmt(deck.meeting_date)}` : ''}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
          {order.map((k, idx) => (
            <span key={k} onClick={() => { setI(idx); setAdding(false) }} title={getSlide(k)?.name}
              style={{ width: idx === i ? 26 : 8, height: 8, borderRadius: 50, cursor: 'pointer',
                background: idx === i ? 'var(--gold)'
                  : fillOf(k) >= 1 ? 'var(--green)' : 'var(--border)',
                transition: 'width .15s' }} />
          ))}
        </div>
        <button onClick={onClose} className="btn-icon" title="Exit"><X size={17} /></button>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Section rail */}
        <div style={{ width: 196, flexShrink: 0, borderRight: '1px solid var(--border-light)',
          overflowY: 'auto', padding: '14px 10px' }}>
          {order.map((k, idx) => {
            const active = idx === i
            const pct = fillOf(k)
            return (
              <div key={k} onClick={() => { setI(idx); setAdding(false) }}
                style={{ padding: '9px 12px', borderRadius: 9, cursor: 'pointer', marginBottom: 2,
                  background: active ? 'var(--navy)' : 'transparent',
                  color: active ? '#fff' : 'var(--text-secondary)',
                  fontSize: 13, fontWeight: active ? 600 : 400,
                  display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ width: 6, height: 6, borderRadius: 50, flexShrink: 0,
                  background: pct >= 1 ? 'var(--green)' : pct > 0 ? 'var(--gold)' : 'var(--border)' }} />
                {getSlide(k)?.name}
              </div>
            )
          })}
        </div>

        {/* Page */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '38px 46px' }}>
          <div style={{ maxWidth: 840, margin: '0 auto' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 42, color: 'var(--navy)',
              lineHeight: 1.1, marginBottom: def?.subtitle ? 8 : 26 }}>
              {def?.name}
            </div>
            {def?.subtitle && (
              <div style={{ fontSize: 15.5, color: 'var(--text-secondary)', marginBottom: 28,
                maxWidth: 640, lineHeight: 1.5 }}>{def.subtitle}</div>
            )}
            {main()}
            <div style={{ height: 60 }} />
          </div>
        </div>

        {/* Side panel — stays put across sections */}
        <div style={{ width: 316, flexShrink: 0, borderLeft: '1px solid var(--border-light)',
          display: 'flex', flexDirection: 'column', background: 'var(--cream)' }}>
          <div style={{ display: 'flex', borderBottom: '1px solid var(--border-light)' }}>
            {[['notes', 'Notes'], ['brand', 'Brand'], ['capture', 'Capture']].map(([k, l]) => (
              <button key={k} onClick={() => setPanel(k)}
                style={{ flex: 1, padding: '11px 4px', fontSize: 12.5, cursor: 'pointer',
                  background: 'none', border: 'none',
                  fontWeight: panel === k ? 700 : 500,
                  color: panel === k ? 'var(--navy)' : 'var(--text-muted)',
                  borderBottom: panel === k ? '2px solid var(--gold)' : '2px solid transparent' }}>
                {l}
              </button>
            ))}
          </div>

          <div style={{ flex: 1, overflowY: 'auto', padding: 15 }}>
            {panel === 'notes' && (
              <>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 9 }}>
                  Internal. Stays here whatever section you're on.
                </div>
                <Editable value={deck.consultation_notes} size={13.5} lines
                  placeholder="What they said, what surprised you, anything agreed that isn't in the contract."
                  onSave={val => onSaveDeck({ consultation_notes: val || null })} />
              </>
            )}

            {panel === 'brand' && (
              <>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Saves to {client?.name}'s profile as you type.
                </div>
                {BRAND_FIELDS.map(f => (
                  <div key={f.key} style={{ marginBottom: 13 }}>
                    <div style={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase',
                      letterSpacing: 0.4, color: 'var(--navy)', marginBottom: 3 }}>{f.label}</div>
                    <Editable value={client?.[f.key]} size={12.5} placeholder={f.hint}
                      onSave={val => onSaveClient({ [f.key]: val || null })} />
                  </div>
                ))}
              </>
            )}

            {panel === 'capture' && (
              <>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 10 }}>
                  Add a goal or a delivery date without leaving this section.
                </div>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.4, color: 'var(--navy)', marginBottom: 6 }}>Quick goal</div>
                  <input className="form-control" style={{ fontSize: 12.5, marginBottom: 5 }}
                    placeholder="What are we aiming for?" value={goalDraft.title}
                    onChange={e => setGoalDraft({ ...goalDraft, title: e.target.value })} />
                  <input className="form-control" style={{ fontSize: 12.5, marginBottom: 5 }}
                    placeholder="Where they are today" value={goalDraft.current_state}
                    onChange={e => setGoalDraft({ ...goalDraft, current_state: e.target.value })} />
                  <button className="btn btn-gold btn-sm" style={{ width: '100%' }}
                    disabled={!goalDraft.title.trim()}
                    onClick={async () => {
                      const ok = await onAddGoal(goalDraft)
                      if (ok !== false) setGoalDraft({ title: '', current_state: '', target_text: '', target_date: '' })
                    }}>Add goal</button>
                </div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    letterSpacing: 0.4, color: 'var(--navy)', marginBottom: 6 }}>Delivery date</div>
                  <input className="form-control" style={{ fontSize: 12.5, marginBottom: 5 }}
                    placeholder="What are we delivering?" value={tlDraft.what}
                    onChange={e => setTlDraft({ ...tlDraft, what: e.target.value })} />
                  <input className="form-control" type="date" style={{ fontSize: 12.5, marginBottom: 5 }}
                    value={tlDraft.due_date}
                    onChange={e => setTlDraft({ ...tlDraft, due_date: e.target.value })} />
                  <button className="btn btn-gold btn-sm" style={{ width: '100%' }}
                    disabled={!tlDraft.what.trim() || !tlDraft.due_date}
                    onClick={async () => {
                      const ok = await onAddCommitment(tlDraft)
                      if (ok !== false) setTlDraft({ what: '', due_date: '' })
                    }}>Add date</button>
                </div>
              </>
            )}
          </div>

          <div style={{ padding: '10px 15px', borderTop: '1px solid var(--border-light)',
            display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost btn-sm" disabled={i === 0} onClick={() => setI(i - 1)}
              style={{ flex: 1 }}><ChevronLeft size={13} /> Back</button>
            <button className="btn btn-ghost btn-sm" disabled={i === order.length - 1}
              onClick={() => setI(i + 1)} style={{ flex: 1 }}>Next <ChevronRight size={13} /></button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Slide editor ────────────────────────────────────────────────────────────
function SlideEditor({ slideKey, data, onChange, onRemove, onMove, isFirst, isLast, client, onSaveClient }) {
  const def = getSlide(slideKey)
  const [open, setOpen] = useState(false)
  if (!def) return null

  return (
    <div style={{ border: '1px solid var(--border-light)', borderRadius: 10, marginBottom: 8 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
        cursor: 'pointer' }} onClick={() => setOpen(o => !o)}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy)' }}>{def.name}</div>
          {def.purpose && (
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{def.purpose}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 3 }} onClick={e => e.stopPropagation()}>
          <button className="btn-icon" disabled={isFirst} onClick={() => onMove(-1)} title="Move up">↑</button>
          <button className="btn-icon" disabled={isLast} onClick={() => onMove(1)} title="Move down">↓</button>
          {!def.always && (
            <button className="btn-icon" onClick={onRemove} title="Remove slide"><Trash2 size={13} /></button>
          )}
        </div>
        <ChevronRight size={14} style={{ color: 'var(--text-muted)',
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }} />
      </div>

      {open && (
        <div style={{ padding: '0 12px 12px', borderTop: '1px solid var(--border-light)' }}>
          {(def.isGoals || def.isTimeline) && (
            <div style={{ fontSize: 12, color: 'var(--text-muted)', margin: '10px 0',
              padding: '8px 10px', background: 'var(--cream)', borderRadius: 8 }}>
              This slide builds itself from the {def.isGoals ? 'Goals' : 'Timeline'} tab —
              whatever you add there appears here and stays connected to the 30/60/90 check-ins.
            </div>
          )}
          {def.isBrand && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10,
                padding: '8px 10px', background: 'var(--cream)', borderRadius: 8 }}>
                Fill these in on the call — each saves straight to the client's Brand Identity
                on their profile. Nothing to write up afterwards.
              </div>
              {BRAND_FIELDS.map(f => (
                <div className="form-group" key={f.key}>
                  <label className="form-label">{f.label}</label>
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>{f.hint}</div>
                  <textarea className="form-control" rows={2}
                    style={{ fontSize: 12.5, resize: 'vertical' }}
                    defaultValue={client?.[f.key] || ''}
                    onBlur={e => {
                      if (e.target.value !== (client?.[f.key] || '')) {
                        onSaveClient({ [f.key]: e.target.value || null })
                      }
                    }} />
                </div>
              ))}
            </div>
          )}

          {def.fields.map(f => (
            <div className="form-group" key={f.key} style={{ marginTop: 10 }}>
              <label className="form-label">{f.label}</label>
              {f.type === 'lines' && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
                  One per line — each becomes a bullet.
                </div>
              )}
              {f.type === 'text' ? (
                <input className="form-control" value={data[f.key] || ''}
                  onChange={e => onChange(f.key, e.target.value)} />
              ) : (
                <textarea className="form-control" rows={f.type === 'lines' ? 4 : 3}
                  style={{ fontSize: 12.5, resize: 'vertical' }}
                  value={data[f.key] || ''} onChange={e => onChange(f.key, e.target.value)} />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Admin checklist ─────────────────────────────────────────────────────────
function ChecklistTab({ deck, onSave }) {
  const state = deck.checklist || {}
  const notes = deck.checklist_notes || {}
  const st = checklistStats(state)

  const set = (key, val) => {
    const next = { ...state }
    if (next[key] === val) delete next[key]   // click again to clear
    else next[key] = val
    onSave({ checklist: next })
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        {[
          { l: 'Answered', v: `${st.answered}/${st.total}`, c: 'var(--navy)' },
          { l: 'We have it', v: st.have, c: 'var(--green)' },
          { l: 'Still needed', v: st.need.length, c: st.need.length ? '#c4962a' : 'var(--text-muted)' },
          { l: 'Never asked', v: st.unanswered.length,
            c: st.unanswered.length ? 'var(--text-muted)' : 'var(--green)' },
        ].map(x => (
          <div key={x.l} style={{ flex: 1, minWidth: 110, padding: '10px 12px', borderRadius: 9,
            border: '1px solid var(--border-light)' }}>
            <div style={{ fontFamily: 'var(--font-display)', fontSize: 21, color: x.c }}>{x.v}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{x.l}</div>
          </div>
        ))}
      </div>

      {st.need.length > 0 && (
        <div style={{ fontSize: 12.5, padding: '10px 12px', borderRadius: 8, marginBottom: 14,
          background: '#fff8e1', color: '#8a6000' }}>
          <strong>To chase after the meeting:</strong>{' '}
          {st.need.map(k => CHECKLIST.flatMap(g => g.items).find(i => i.key === k)?.label).join(' · ')}
        </div>
      )}

      {CHECKLIST.map(g => (
        <div key={g.group} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 7 }}>{g.group}</div>
          {g.items.map(it => (
            <div key={it.key} style={{ display: 'flex', alignItems: 'center', gap: 9,
              padding: '6px 0', borderBottom: '1px solid var(--border-light)', flexWrap: 'wrap' }}>
              <div style={{ flex: 1, minWidth: 170, fontSize: 13 }}>{it.label}</div>
              <div style={{ display: 'flex', gap: 4 }}>
                {Object.entries(CHECKLIST_STATES).map(([k, v]) => {
                  const on = state[it.key] === k
                  return (
                    <button key={k} onClick={() => set(it.key, k)}
                      style={{ fontSize: 11.5, padding: '3px 9px', borderRadius: 50, cursor: 'pointer',
                        border: `1px solid ${on ? v.color : 'var(--border)'}`,
                        background: on ? v.color : '#fff',
                        color: on ? '#fff' : 'var(--text-secondary)' }}>
                      {v.label}
                    </button>
                  )
                })}
              </div>
              <input className="form-control" style={{ width: 180, fontSize: 12 }}
                placeholder="Note" defaultValue={notes[it.key] || ''}
                onBlur={e => {
                  if (e.target.value !== (notes[it.key] || '')) {
                    onSave({ checklist_notes: { ...notes, [it.key]: e.target.value || undefined } })
                  }
                }} />
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

// ── Main ────────────────────────────────────────────────────────────────────
export default function KickoffDecksView({ allClients, profiles, currentUser, onClientsChanged }) {
  const toast = useToast()
  const [decks, setDecks] = useState([])
  const [goals, setGoals] = useState([])
  const [commitments, setCommitments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [tab, setTab] = useState('slides')
  const [presenting, setPresenting] = useState(false)
  const [creating, setCreating] = useState(false)
  const [newClientId, setNewClientId] = useState('')
  const [newDate, setNewDate] = useState('')
  const [goalDraft, setGoalDraft] = useState({
    title: '', current_state: '', target_text: '', target: '', target_date: '', metric: '', notes: '' })
  const [tlDraft, setTlDraft] = useState({ what: '', due_date: '' })
  const [confirmDelete, setConfirmDelete] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    const [dRes, gRes, cRes] = await Promise.all([
      supabase.from('kickoff_decks').select('*').order('created_at', { ascending: false }),
      supabase.from('client_goals').select('*').order('sort_order'),
      supabase.from('client_commitments').select('*').order('due_date'),
    ])
    if (dRes.error) toast('Could not load decks: ' + dRes.error.message, 'error')
    setDecks(dRes.data || [])
    setGoals(gRes.error ? [] : (gRes.data || []))
    setCommitments(cRes.error ? [] : (cRes.data || []))
    setLoading(false)
  }, [toast])

  useEffect(() => { load() }, [load])

  const deck = decks.find(d => d.id === selectedId)
  const client = deck ? allClients.find(c => c.id === deck.client_id) : null
  const deckGoals = deck ? goals.filter(g => g.client_id === deck.client_id) : []
  const deckCommitments = deck ? commitments.filter(c => c.client_id === deck.client_id) : []
  const profileName = id => profiles.find(p => p.id === id)?.full_name || ''

  const save = async (fields) => {
    if (!deck) return false
    setDecks(ds => ds.map(d => d.id === deck.id ? { ...d, ...fields } : d))   // optimistic
    const { data, error } = await supabase
      .from('kickoff_decks').update(fields).eq('id', deck.id).select()
    if (error) {
      console.error('kickoff_decks update failed', { fields, error })
      toast('Could not save: ' + error.message, 'error'); load(); return false
    }
    if (!data || data.length === 0) {
      toast('Save was blocked — check your access to this deck', 'error'); load(); return false
    }
    return true
  }

  // Brand identity fields live on the clients table, so the kickoff writes
  // them to the client record rather than keeping a second copy on the deck.
  // Writes brand fields to the client record.
  //
  // .select() matters here: a Postgres row-level-security block returns NO
  // error and NO rows, so without checking the returned rows a blocked write
  // looks identical to a successful one. That's what made this fail silently.
  const saveClient = async (fields, quiet = false) => {
    if (!deck) return false
    const { data, error } = await supabase
      .from('clients').update(fields).eq('id', deck.client_id).select()

    if (error) {
      console.error('clients update failed', { fields, error })
      toast('Could not save: ' + error.message, 'error')
      return false
    }
    if (!data || data.length === 0) {
      console.error('clients update returned no rows — blocked by RLS?', { fields })
      toast('Save was blocked — you may not have permission to edit this client', 'error')
      return false
    }
    if (!quiet) toast('Saved to their profile', 'success')
    onClientsChanged && onClientsChanged()
    return true
  }

  const deleteDeck = async (d) => {
    const { error } = await supabase.from('kickoff_decks').delete().eq('id', d.id)
    if (error) { toast('Could not delete: ' + error.message, 'error'); return }
    // The Documents row points at a deck that no longer exists, so clear it too.
    await supabase.from('client_documents').delete()
      .eq('doc_type', 'kickoff_deck').eq('url', `/kickoff/${d.id}`)
    toast('Deck deleted', 'success')
    setConfirmDelete(null)
    if (selectedId === d.id) setSelectedId(null)
    load()
  }

  const create = async () => {
    const c = allClients.find(x => x.id === newClientId)
    if (!c) return
    const order = defaultSlidesFor(c)
    // Prefill what we already know about the client so the deck starts useful.
    const slides = {}
    order.forEach(k => {
      const def = getSlide(k)
      slides[k] = {}
      def.fields.forEach(f => {
        if (f.default) slides[k][f.key] = f.default
        if (f.auto === 'ad') slides[k][f.key] = profileName(c.account_director_id)
        if (f.auto === 'am') slides[k][f.key] = profileName(c.account_manager_id)
      })
    })
    const { data, error } = await supabase.from('kickoff_decks').insert({
      client_id: c.id,
      title: `${c.name} — Kick Off Meeting`,
      meeting_date: newDate || null,
      slide_order: order,
      slides,
      created_by: currentUser?.id || null,
    }).select().single()
    if (error) { toast('Could not create: ' + error.message, 'error'); return }
    toast('Deck created', 'success')
    setCreating(false); setNewClientId(''); setNewDate('')
    await load()
    setSelectedId(data.id)
  }

  // Goals and timeline write to the shared tables, not to the deck — so the
  // 30/60/90 check-ins measure the same records.
  // Shared by the Goals tab and the meeting surface.
  const addGoalFrom = async (d) => {
    if (!d.title?.trim() || !deck) return false
    const { data, error } = await supabase.from('client_goals').insert({
      client_id: deck.client_id,
      title: d.title.trim(),
      goal_type: d.target ? 'number' : 'milestone',
      current_state: d.current_state?.trim() || null,
      target_text: d.target_text?.trim() || null,
      target: d.target ? Number(d.target) : null,
      target_date: d.target_date || null,
      metric: d.metric?.trim() || null,
      notes: d.notes?.trim() || null,
      is_primary: deckGoals.length === 0,
      created_by: currentUser?.id || null,
    }).select()
    if (error) { toast('Could not add goal: ' + error.message, 'error'); return false }
    if (!data?.length) { toast('Goal was blocked — check your access', 'error'); return false }
    await load()
    return true
  }

  const addCommitmentFrom = async (d) => {
    if (!d.what?.trim() || !d.due_date || !deck) return false
    const { data, error } = await supabase.from('client_commitments').insert({
      client_id: deck.client_id, what: d.what.trim(), due_date: d.due_date,
      source: 'kickoff', promised_by: currentUser?.id || null,
    }).select()
    if (error) { toast('Could not add: ' + error.message, 'error'); return false }
    if (!data?.length) { toast('Blocked — check your access', 'error'); return false }
    await load()
    return true
  }

  const addGoal = async () => {
    if (!goalDraft.title.trim() || !deck) return
    const ok = await addGoalFrom(goalDraft)
    if (ok !== false) setGoalDraft({ title: '', current_state: '', target_text: '', target: '',
      target_date: '', metric: '', notes: '' })
  }

  const addCommitment = async () => {
    if (!tlDraft.what.trim() || !tlDraft.due_date || !deck) return
    const ok = await addCommitmentFrom(tlDraft)
    if (ok !== false) setTlDraft({ what: '', due_date: '' })
  }

  // Marking delivered files the deck in the client's Documents tab.
  const markDelivered = async () => {
    if (!deck) return
    await save({ status: 'delivered', delivered_on: new Date().toISOString().slice(0, 10) })
    const { error } = await supabase.from('client_documents').insert({
      client_id: deck.client_id,
      label: `Kick Off Meeting — ${deck.meeting_date ? fmt(deck.meeting_date) : 'deck'}`,
      url: `/kickoff/${deck.id}`,
      doc_type: 'kickoff_deck',
      notes: 'Kickoff deck, goals and admin checklist. Opens in the Kickoff Decks section.',
      created_by: currentUser?.id || null,
    })
    if (error) toast('Marked delivered, but filing to Documents failed: ' + error.message, 'error')
    else toast('Delivered and filed to the client\'s Documents', 'success')
  }

  const setSlideField = (slideKey, fieldKey, value) => {
    save({ slides: { ...(deck.slides || {}), [slideKey]: { ...(deck.slides?.[slideKey] || {}), [fieldKey]: value } } })
  }

  const moveSlide = (idx, dir) => {
    const order = [...deck.slide_order]
    const j = idx + dir
    if (j < 0 || j >= order.length) return
    ;[order[idx], order[j]] = [order[j], order[idx]]
    save({ slide_order: order })
  }

  const addSlide = (key) => save({ slide_order: [...(deck.slide_order || []), key] })
  const removeSlide = (key) => save({ slide_order: deck.slide_order.filter(k => k !== key) })

  if (loading) return <div style={{ padding: 40, color: 'var(--text-muted)' }}>Loading…</div>

  const available = SLIDE_TEMPLATES.filter(s => !deck?.slide_order?.includes(s.key))

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 22, color: 'var(--navy)' }}>
          🎤 Kickoff Decks
          <span style={{ fontSize: 14, color: 'var(--text-muted)', fontWeight: 400 }}>
            {' '}· {decks.length}
          </span>
        </div>
        <button className="btn btn-gold btn-sm" onClick={() => setCreating(c => !c)}>
          <Plus size={13} /> New deck
        </button>
      </div>

      {creating && (
        <div className="card" style={{ marginBottom: 14 }}>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Client</label>
                <select className="form-control" value={newClientId}
                  onChange={e => setNewClientId(e.target.value)}>
                  <option value="">— Select —</option>
                  {allClients.filter(c => !c.archived_at)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Meeting date</label>
                <input className="form-control" type="date" value={newDate}
                  onChange={e => setNewDate(e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 10 }}>
              Slides are preselected from the services on the client record, and the AD and AM
              names fill in automatically. Everything is editable.
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setCreating(false)}>Cancel</button>
              <button className="btn btn-gold btn-sm" disabled={!newClientId} onClick={create}>Create</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Deck list */}
        <div className="card" style={{ width: 240, flexShrink: 0 }}>
          <div className="card-header"><span className="card-title">Decks</span></div>
          <div style={{ padding: 8 }}>
            {decks.length === 0 && (
              <div style={{ fontSize: 12.5, color: 'var(--text-muted)', fontStyle: 'italic', padding: 10 }}>
                No decks yet.
              </div>
            )}
            {decks.map(d => {
              const c = allClients.find(x => x.id === d.client_id)
              const active = d.id === selectedId
              const st = DECK_STATUS[d.status]
              return (
                <div key={d.id} onClick={() => { setSelectedId(d.id); setTab('slides') }}
                  style={{ padding: '10px 12px', borderRadius: 8, cursor: 'pointer', marginBottom: 4,
                    background: active ? 'var(--navy)' : 'transparent',
                    color: active ? '#fff' : 'var(--text-primary)' }}>
                  <div style={{ fontSize: 13, fontWeight: active ? 600 : 500, overflow: 'hidden',
                    textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c?.name || 'Unknown'}</div>
                  <div style={{ fontSize: 11.5, opacity: active ? 0.8 : 0.65 }}>
                    {d.meeting_date ? fmt(d.meeting_date) : 'No date'}
                    {' · '}<span style={{ color: active ? '#fff' : st.color }}>{st.label}</span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Deck detail */}
        <div style={{ flex: 1, minWidth: 480 }}>
          {!deck ? (
            <div className="empty-state">
              <Presentation size={40} className="empty-state-icon" />
              <h3>No deck selected</h3>
              <p>Pick a deck, or create one for a client who is already onboarded.</p>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12,
                flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontSize: 19, color: 'var(--navy)' }}>
                  {client?.name}
                </span>
                <span style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                  {deck.meeting_date ? fmt(deck.meeting_date) : 'No date set'}
                </span>
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 7 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setPresenting(true)}
                    disabled={!deck.slide_order?.length}>
                    <Play size={13} /> Run meeting
                  </button>
                  {deck.status !== 'delivered' && (
                    <button className="btn btn-gold btn-sm" onClick={markDelivered}>
                      <Check size={13} /> Mark delivered
                    </button>
                  )}
                  <button className="btn-icon" title="Delete this deck"
                    onClick={() => setConfirmDelete(deck)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>

              <div className="tabs" style={{ marginBottom: 14 }}>
                {[
                  ['slides', <><FileText size={12} /> Slides</>],
                  ['goals', <><Target size={12} /> Goals</>],
                  ['timeline', <><CalendarDays size={12} /> Timeline</>],
                  ['checklist', <><ClipboardList size={12} /> Checklist</>],
                ].map(([k, label]) => (
                  <button key={k} className={`tab ${tab === k ? 'active' : ''}`}
                    onClick={() => setTab(k)}>{label}</button>
                ))}
              </div>

              {tab === 'slides' && (
                <>
                  <div className="card" style={{ marginBottom: 14 }}>
                    <div className="card-header">
                      <span className="card-title">Slides · {deck.slide_order?.length || 0}</span>
                    </div>
                    <div className="card-body">
                      {(deck.slide_order || []).map((k, i) => (
                        <SlideEditor key={k} slideKey={k}
                          data={deck.slides?.[k] || {}}
                          onChange={(fk, v) => setSlideField(k, fk, v)}
                          onRemove={() => removeSlide(k)}
                          onMove={dir => moveSlide(i, dir)}
                          isFirst={i === 0} isLast={i === deck.slide_order.length - 1}
                          client={client} onSaveClient={saveClient} />
                      ))}

                      {available.length > 0 && (
                        <div style={{ marginTop: 12, paddingTop: 10,
                          borderTop: '1px solid var(--border-light)' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                            letterSpacing: 0.4, color: 'var(--text-muted)', marginBottom: 6 }}>
                            Add a slide
                          </div>
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {available.map(s => (
                              <button key={s.key} className="btn btn-ghost btn-sm"
                                onClick={() => addSlide(s.key)}>
                                <Plus size={11} /> {s.name}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Preview */}
                  {deck.slide_order?.length > 0 && (
                    <div className="card">
                      <div className="card-header"><span className="card-title">Preview</span></div>
                      <div className="card-body" style={{ display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 }}>
                        {deck.slide_order.map(k => (
                          <SlideView key={k} slideKey={k}
                            data={{ ...(deck.slides?.[k] || {}), _meeting_date: deck.meeting_date }}
                            client={client} goals={deckGoals} commitments={deckCommitments} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}

              {tab === 'goals' && (
                <div className="card">
                  <div className="card-header"><span className="card-title">Goals</span></div>
                  <div className="card-body">
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12 }}>
                      These go on the Goals slide and become the client's goals in First 90 Days —
                      the same records the day 30, 60 and 90 check-ins measure against. Leave the
                      target blank for a goal that is simply done or not done.
                    </div>

                    {deckGoals.map(g => (
                      <div key={g.id} style={{ padding: '9px 11px', border: '1px solid var(--border-light)',
                        borderRadius: 9, marginBottom: 7 }}>
                        <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--navy)' }}>{g.title}</div>
                        {g.current_state && (
                          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                            Today: {g.current_state}
                          </div>
                        )}
                        {g.target_text && (
                          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 2 }}>
                            Aiming for: {g.target_text}
                          </div>
                        )}
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
                          {g.target != null && `Target ${g.target}`}
                          {g.metric && g.target != null ? ` ${g.metric}` : ''}
                          {g.target_date ? `${g.target != null ? ' · ' : ''}by ${fmt(g.target_date)}` : ''}
                        </div>
                        {g.notes && (
                          <div style={{ fontSize: 12.5, lineHeight: 1.5, marginTop: 6,
                            padding: '7px 9px', background: 'var(--cream)', borderRadius: 7,
                            whiteSpace: 'pre-wrap' }}>{g.notes}</div>
                        )}
                      </div>
                    ))}

                    <div style={{ border: '1px dashed var(--border)', borderRadius: 9,
                      padding: 12, marginTop: 10 }}>
                      <div className="form-group">
                        <label className="form-label">What are we aiming for?</label>
                        <input className="form-control" value={goalDraft.title}
                          onChange={e => setGoalDraft({ ...goalDraft, title: e.target.value })}
                          placeholder="More new client inquiries" />
                      </div>

                      <div className="form-group">
                        <label className="form-label">Where are they today?</label>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
                          In their words. "No idea, they've never tracked it" is a real answer
                          and worth writing down.
                        </div>
                        <textarea className="form-control" rows={2}
                          style={{ fontSize: 12.5, resize: 'vertical' }}
                          value={goalDraft.current_state}
                          onChange={e => setGoalDraft({ ...goalDraft, current_state: e.target.value })}
                          placeholder="Maybe 3 or 4 inquiries a month, mostly word of mouth" />
                      </div>

                      <div className="form-group">
                        <label className="form-label">What does success look like?</label>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
                          Describe it. A number is optional and goes below.
                        </div>
                        <textarea className="form-control" rows={2}
                          style={{ fontSize: 12.5, resize: 'vertical' }}
                          value={goalDraft.target_text}
                          onChange={e => setGoalDraft({ ...goalDraft, target_text: e.target.value })}
                          placeholder="Enough steady inquiries to fill two therapists' caseloads" />
                      </div>

                      <div className="form-row">
                        <div className="form-group">
                          <label className="form-label">
                            Number to hit <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                          </label>
                          <input className="form-control" type="number" value={goalDraft.target}
                            onChange={e => setGoalDraft({ ...goalDraft, target: e.target.value })}
                            placeholder="Leave blank if there isn't one" />
                        </div>
                        <div className="form-group">
                          <label className="form-label">By when</label>
                          <input className="form-control" type="date" value={goalDraft.target_date}
                            onChange={e => setGoalDraft({ ...goalDraft, target_date: e.target.value })} />
                        </div>
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          How we measure it <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                        </label>
                        <input className="form-control" value={goalDraft.metric}
                          onChange={e => setGoalDraft({ ...goalDraft, metric: e.target.value })}
                          placeholder="Call tracking, form fills, booked consults" />
                      </div>

                      <div className="form-group">
                        <label className="form-label">
                          Notes <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(optional)</span>
                        </label>
                        <textarea className="form-control" rows={2}
                          style={{ fontSize: 12.5, resize: 'vertical' }}
                          value={goalDraft.notes}
                          onChange={e => setGoalDraft({ ...goalDraft, notes: e.target.value })}
                          placeholder="Anything else said about this goal." />
                      </div>

                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="btn btn-gold btn-sm" disabled={!goalDraft.title.trim()}
                          onClick={addGoal}>Add goal</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'timeline' && (
                <div className="card">
                  <div className="card-header"><span className="card-title">Timeline</span></div>
                  <div className="card-body">
                    <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginBottom: 12 }}>
                      Delivery dates set in the meeting. Each is tracked on the client
                      record, so we can see later whether we hit the dates we set.
                    </div>

                    {deckCommitments.map(c => (
                      <div key={c.id} style={{ display: 'flex', gap: 12, alignItems: 'baseline',
                        padding: '7px 0', borderBottom: '1px solid var(--border-light)' }}>
                        <div style={{ width: 110, fontSize: 12.5, fontWeight: 600, color: 'var(--gold)' }}>
                          {fmt(c.due_date)}
                        </div>
                        <div style={{ flex: 1, fontSize: 13 }}>{c.what}</div>
                        <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{c.status}</div>
                      </div>
                    ))}

                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginTop: 12,
                      flexWrap: 'wrap' }}>
                      <div style={{ flex: 1, minWidth: 200 }}>
                        <label className="form-label">What are we delivering?</label>
                        <input className="form-control" value={tlDraft.what}
                          onChange={e => setTlDraft({ ...tlDraft, what: e.target.value })}
                          placeholder="Two homepage design options" />
                      </div>
                      <div style={{ width: 160 }}>
                        <label className="form-label">By when</label>
                        <input className="form-control" type="date" value={tlDraft.due_date}
                          onChange={e => setTlDraft({ ...tlDraft, due_date: e.target.value })} />
                      </div>
                      <button className="btn btn-gold btn-sm"
                        disabled={!tlDraft.what.trim() || !tlDraft.due_date}
                        onClick={addCommitment}>Add</button>
                    </div>
                  </div>
                </div>
              )}

              {tab === 'checklist' && (
                <div className="card">
                  <div className="card-header">
                    <span className="card-title">Admin checklist</span>
                  </div>
                  <div className="card-body">
                    <ChecklistTab deck={deck} onSave={save} />

                    <div className="form-group" style={{ marginTop: 16 }}>
                      <label className="form-label">Notes from the meeting</label>
                      <textarea className="form-control" rows={4}
                        style={{ fontSize: 12.5, resize: 'vertical' }}
                        defaultValue={deck.consultation_notes || ''}
                        onBlur={e => {
                          if (e.target.value !== (deck.consultation_notes || '')) {
                            save({ consultation_notes: e.target.value || null })
                          }
                        }}
                        placeholder="What they said, what surprised you, anything agreed that isn't in the contract." />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {confirmDelete && (
        <div className="modal-overlay"
          onClick={e => e.target === e.currentTarget && setConfirmDelete(null)}>
          <div className="modal" style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <span className="modal-title">Delete this deck?</span>
              <button className="btn-icon" onClick={() => setConfirmDelete(null)}><X size={16} /></button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 13.5, lineHeight: 1.6 }}>
                This deletes the kickoff deck for{' '}
                <strong>{allClients.find(c => c.id === confirmDelete.client_id)?.name}</strong>
                {confirmDelete.meeting_date ? ` (${fmt(confirmDelete.meeting_date)})` : ''} —
                its slides, checklist and meeting notes. This cannot be undone.
              </p>
              <div style={{ fontSize: 12.5, padding: '9px 11px', borderRadius: 8,
                background: 'var(--lightgreen-bg)', color: '#1f5c50', marginTop: 10 }}>
                Goals, delivery dates and brand answers are kept — they live on the client
                record, not on the deck.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setConfirmDelete(null)}>Cancel</button>
              <button className="btn btn-danger" onClick={() => deleteDeck(confirmDelete)}>
                <Trash2 size={13} /> Delete deck
              </button>
            </div>
          </div>
        </div>
      )}

      {presenting && deck && (
        <MeetingMode
          deck={deck} client={client} goals={deckGoals} commitments={deckCommitments}
          onClose={() => setPresenting(false)}
          onSaveClient={f => saveClient(f, true)}
          onSaveSlide={(sk, fk, v) => setSlideField(sk, fk, v)}
          onSaveDeck={save}
          onAddGoal={addGoalFrom}
          onAddCommitment={addCommitmentFrom}
        />
      )}
    </div>
  )
}